import React, { useState, useEffect, useRef } from 'react'
import { useNutritionStore } from '@/stores/nutritionStore'
import { useAuthStore } from '@/stores/authStore'
import { useDataFetcher } from '@/hooks/useApiManager'
import { apiService } from '@/services/api'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Progress } from '@/components/ui/progress'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import {
  Plus,
  Minus,
  Save,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  StickyNote,
  Shuffle,
  UtensilsCrossed,
  ShoppingBag,
  Printer,
  Loader2,
  Trash2,
  AlertCircle
} from 'lucide-react'
import {
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts'

const ClientNutrition = () => {
  const { companyUrl } = useAuthStore()
  const [expandedMeals, setExpandedMeals] = useState({})
  const [editingQuantity, setEditingQuantity] = useState({})
  const [noteModal, setNoteModal] = useState({ open: false, food: null, meal: null, mealplan: null })
  const [noteDraft, setNoteDraft] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)
  const [showNutritionChart, setShowNutritionChart] = useState(false)
  const [staticLinks, setStaticLinks] = useState({})
  const [shoppingListLoading, setShoppingListLoading] = useState(false)
  const [printLoading, setPrintLoading] = useState(false)
  const [searchQueries, setSearchQueries] = useState({})
  const [searchResults, setSearchResults] = useState({})
  const [searchLoading, setSearchLoading] = useState({})
  const [searchCategories, setSearchCategories] = useState({})
  const [searchSelectLoading, setSearchSelectLoading] = useState({})
  const [deleteLoading, setDeleteLoading] = useState({})
  const [programsReady, setProgramsReady] = useState(false)
  const searchDebounceRefs = useRef({})

  const {
    programs,
    selectedProgram,
    selectedDate,
    nutritionData,
    clientDetails,
    loggedFoods,
    foodNotes,
    foodQuantities,
    isLoading,
    error,
    fetchClientPrograms,
    fetchNutritionData,
    setFoodLogged,
    logSingleFood,
    updateFoodNote,
    updateFoodQuantity,
    setSelectedProgram,
    setSelectedDate,
    getLoggedNutritionTotals,
    getScheduledNutritionTotals
  } = useNutritionStore()

  const { toast } = useToast()

  // Reset readiness whenever the company context changes, so the nutrition
  // fetch below never runs against a stale persisted program while the
  // fresh program list is still in flight.
  useEffect(() => {
    setProgramsReady(false)
  }, [companyUrl])

  // Load initial data with proper API management
  useDataFetcher(
    async (signal) => {
      return await fetchClientPrograms(companyUrl)
    },
    [companyUrl],
    {
      enabled: !!companyUrl,
      requestKey: `page:fetchClientPrograms:${companyUrl}`,
      onSuccess: () => setProgramsReady(true),
      onError: () => setProgramsReady(true)
    }
  )

  useEffect(() => {
    if (!companyUrl) return
    const controller = new AbortController()
    const fetchStaticPages = async () => {
      try {
        const response = await apiService.fetchStaticPages(companyUrl, controller.signal)
        if (response.success) {
          setStaticLinks(response.data || {})
        }
      } catch (error) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
          console.error('Failed to fetch static pages', error)
        }
      }
    }

    fetchStaticPages()
    return () => controller.abort()
  }, [companyUrl])

  useEffect(() => {
    return () => {
      Object.values(searchDebounceRefs.current).forEach(clearTimeout)
    }
  }, [])

  useEffect(() => {
    if (!Array.isArray(nutritionData?.mealplans)) return
    const loggedMap = {}
    const qtyMap = {}
    const noteMap = {}

    nutritionData.mealplans.forEach(plan => {
      if (!Array.isArray(plan?.meals)) return
      plan.meals.forEach(meal => {
        if (!Array.isArray(meal?.foods)) return
        meal.foods.forEach(food => {
          const id = food.mealfood_id || food.lognutritionsinglefoods_id || food.id
          if (!id) return
          loggedMap[id] = Number(food.toggle) === 1
          qtyMap[id] = Number(food.log_qty ?? food.qty ?? 0) || 0
          noteMap[id] = food.clientnote || ''
        })
      })
    })

    useNutritionStore.setState(state => ({
      loggedFoods: { ...state.loggedFoods, ...loggedMap },
      foodQuantities: { ...state.foodQuantities, ...qtyMap },
      foodNotes: { ...state.foodNotes, ...noteMap }
    }))
  }, [nutritionData?.mealplans])

  // Load nutrition data when program or date changes with proper API management.
  // Gated on programsReady so this never fires with a stale persisted
  // selectedProgram before the fresh /getclientprograms response lands.
  useDataFetcher(
    async (signal) => {
      return await fetchNutritionData(companyUrl, selectedDate, selectedProgram.id.toString())
    },
    [selectedProgram?.id, selectedDate, companyUrl, programsReady],
    {
      enabled: !!(selectedProgram && companyUrl && programsReady),
      requestKey: `page:fetchNutritionData:${companyUrl}:${selectedDate}:${selectedProgram?.id}`
    }
  )

  const loggedTotals = getLoggedNutritionTotals()
  const scheduledTotals = getScheduledNutritionTotals()

  const handleQuantityChange = async (food, meal, mealplan, foodId, newQuantity, resolvedLogged, currentNote) => {
    const quantity = parseFloat(newQuantity) || 0
    updateFoodQuantity(foodId, quantity)
    setEditingQuantity(prev => {
      const { [foodId]: _omit, ...rest } = prev
      return rest
    })
    await handleFoodToggle(food, meal, mealplan, {
      toggleOverride: resolvedLogged ? 1 : 0,
      quantityOverride: quantity,
      noteOverride: currentNote
    })
    
    toast({
      title: "Quantity updated",
      description: `Food quantity set to ${quantity}`,
      variant: "success",
    })
  }

  const handleNoteChange = async (food, meal, mealplan, foodId, note, resolvedLogged, currentQuantity) => {
    updateFoodNote(foodId, note)
    await handleFoodToggle(food, meal, mealplan, {
      toggleOverride: resolvedLogged ? 1 : 0,
      quantityOverride: currentQuantity,
      noteOverride: note
    })
    
    toast({
      title: "Note saved",
      description: "Your note has been saved successfully",
      variant: "success",
    })
  }

  const handleFoodToggle = async (food, meal, mealplan, options = {}) => {
    const { toggleOverride = null, quantityOverride, noteOverride } = options
    const stateFoodId = food.mealfood_id || food.lognutritionsinglefoods_id || food.id
    const logFoodId = food.lognutritionsinglefoods_id || food.id || stateFoodId
    const mealFoodId = food.mealfood_id || stateFoodId
    const isLogged = !!loggedFoods[stateFoodId]
    const desiredToggle = toggleOverride !== null && toggleOverride !== undefined
      ? Number(toggleOverride)
      : isLogged ? 0 : 1
    const currentQuantity = quantityOverride ?? foodQuantities[stateFoodId] ?? food.log_qty ?? food.qty ?? 0
    const currentNote = noteOverride ?? (foodNotes[stateFoodId] || food.clientnote || '')

    setFoodLogged(stateFoodId, desiredToggle === 1)

    const result = await logSingleFood(companyUrl, logFoodId, stateFoodId, {
      log_qty: Number(currentQuantity) || 0,
      clientnote: currentNote,
      toggle: desiredToggle,
      meal_id: meal?.meal_id || meal?.id,
      mealplan_id: mealplan?.id,
      program_id: selectedProgram?.id,
      lognutritionsinglefoods_id: food.lognutritionsinglefoods_id || null,
      mealfood_id: mealFoodId
    })

    if (!result?.success) {
      setFoodLogged(stateFoodId, isLogged)
      toast({
        title: "Update failed",
        description: result?.error || "Could not update food log",
        variant: "destructive",
      })
      return
    }

    if (result?.skipped) {
      setFoodLogged(stateFoodId, isLogged)
      return
    }

    toast({
      title: desiredToggle === 1 ? "Food logged!" : "Food removed",
      description: `${food.name} ${desiredToggle === 1 ? 'added to' : 'removed from'} your nutrition log`,
      variant: desiredToggle === 1 ? "success" : "default",
    })
  }

  const resolveFoodState = (food) => {
    const foodId = food.mealfood_id || food.lognutritionsinglefoods_id || food.id
    const resolvedLogged = Object.prototype.hasOwnProperty.call(loggedFoods, foodId)
      ? loggedFoods[foodId]
      : food.toggle === 1
    const resolvedQuantity = Object.prototype.hasOwnProperty.call(foodQuantities, foodId)
      ? foodQuantities[foodId]
      : Number(food.log_qty ?? food.qty) || 0
    const noteValue = foodNotes[foodId] || food.clientnote || ''
    return { foodId, resolvedLogged, resolvedQuantity, noteValue }
  }

  const openNoteModal = (food, meal, mealplan) => {
    const { noteValue } = resolveFoodState(food)
    setNoteDraft(noteValue)
    setNoteModal({ open: true, food, meal, mealplan })
  }

  const closeNoteModal = () => {
    setNoteModal({ open: false, food: null, meal: null, mealplan: null })
    setNoteDraft('')
  }

  const submitNoteModal = async () => {
    if (!noteModal.food) return
    setNoteSaving(true)
    try {
      const { foodId, resolvedLogged, resolvedQuantity } = resolveFoodState(noteModal.food)
      await handleNoteChange(noteModal.food, noteModal.meal, noteModal.mealplan, foodId, noteDraft, resolvedLogged, resolvedQuantity)
      closeNoteModal()
    } finally {
      setNoteSaving(false)
    }
  }

  const handleDeleteCustomFood = async (food, mealplan) => {
    const id = food.lognutritionsinglefoods_id || food.mealfood_id || food.id
    const planId = food.lognutritionsingle_id || food.mealplan_id || mealplan?.lognutritionsingle_id || mealplan?.id
    if (!id || !planId || !companyUrl) return
    setDeleteLoading(prev => ({ ...prev, [id]: true }))
    try {
      const response = await apiService.deleteLogNutritionFood(companyUrl, planId, id)
      if (!response.success) {
        throw new Error(response.error || 'Delete failed')
      }
      await fetchNutritionData(companyUrl, selectedDate, selectedProgram?.id?.toString())
      toast({
        title: 'Food removed',
        description: `${food.name || food.food_name || 'Item'} deleted`,
        variant: 'success'
      })
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.message || 'Could not delete food',
        variant: 'destructive'
      })
      console.error('Delete custom food failed', error)
    } finally {
      setDeleteLoading(prev => {
        const { [id]: _omit, ...rest } = prev
        return rest
      })
    }
  }

  const toggleMealExpansion = (mealId) => {
    setExpandedMeals(prev => ({
      ...prev,
      [mealId]: !prev[mealId]
    }))
  }

  const handleDateChange = (date) => {
    setSelectedDate(date)
  }

  const handleStaticLink = (key) => {
    if (!staticLinks?.[key]) return
    window.open(staticLinks[key], '_blank', 'noopener,noreferrer')
  }

  const handleShoppingListPdf = async () => {
    if (shoppingListLoading || !companyUrl) return
    const programId = selectedProgram?.id
    const clientId = clientDetails?.id
    if (!programId || !clientId) {
      toast({
        title: 'Missing info',
        description: 'Program or client details not available',
        variant: 'destructive'
      })
      return
    }
    setShoppingListLoading(true)
    try {
      const response = await apiService.getShoppingListPdf(companyUrl, programId, clientId)
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch shopping list')
      }
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not open shopping list',
        variant: 'destructive'
      })
      console.error('Shopping list fetch failed', error)
    } finally {
      setShoppingListLoading(false)
    }
  }

  const handlePrintPdf = async () => {
    if (printLoading || !companyUrl) return
    const programId = selectedProgram?.id
    const clientId = clientDetails?.id
    if (!programId || !clientId) {
      toast({
        title: 'Missing info',
        description: 'Program or client details not available',
        variant: 'destructive'
      })
      return
    }
    setPrintLoading(true)
    try {
      const response = await apiService.getMealsPdf(companyUrl, programId, clientId)
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch meals PDF')
      }
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not open meals PDF',
        variant: 'destructive'
      })
      console.error('Meals PDF fetch failed', error)
    } finally {
      setPrintLoading(false)
    }
  }

  const handleSearchQueryChange = (mealId, value) => {
    setSearchQueries(prev => ({ ...prev, [mealId]: value }))
    const trimmed = value.trim()
    const timers = searchDebounceRefs.current
    if (timers[mealId]) {
      clearTimeout(timers[mealId])
      delete timers[mealId]
    }
    if (!trimmed) {
      setSearchResults(prev => {
        const { [mealId]: _omit, ...rest } = prev
        return rest
      })
      setSearchCategories(prev => {
        const { [mealId]: _omit, ...rest } = prev
        return rest
      })
      setSearchLoading(prev => {
        const { [mealId]: _omit, ...rest } = prev
        return rest
      })
      return
    }
    timers[mealId] = setTimeout(() => {
      handleFoodSearch(mealId, trimmed)
    }, 400)
  }

  const handleFoodSearch = async (mealId, queryOverride) => {
    const query = ((queryOverride ?? searchQueries[mealId]) || '').trim()
    if (!query || !companyUrl) return
    setSearchLoading(prev => ({ ...prev, [mealId]: true }))
    try {
      const response = await apiService.searchNutrition(companyUrl, query)
      if (!response.success) {
        throw new Error(response.error || 'Search failed')
      }
      const data = response.data
      const results = Array.isArray(data?.results) ? data.results : []
      const common = results.filter(item => !item.brand_type || item.brand_type === 1)
      const branded = results.filter(item => item.brand_type === 2)
      setSearchResults(prev => ({ ...prev, [mealId]: { common, branded } }))
      setSearchCategories(prev => ({ ...prev, [mealId]: 'common' }))
    } catch (error) {
      toast({
        title: 'Search failed',
        description: 'Could not fetch items',
        variant: 'destructive'
      })
      console.error('Food search failed', error)
    } finally {
      setSearchLoading(prev => ({ ...prev, [mealId]: false }))
    }
  }

  const handleSearchResultSelect = async (meal, mealplan, item) => {
    if (searchSelectLoading[meal.meal_id] || !companyUrl) return
    setSearchSelectLoading(prev => ({ ...prev, [meal.meal_id]: true }))
    try {
      const isBranded = item.brand_type === 2
      const nutrientResponse = await (isBranded
        ? apiService.searchNutritionByScan(companyUrl, item.nix_item_id || item.resource_id || '')
        : apiService.fetchNutritionNutrients(companyUrl, item.item_name || item.food_name || '')
      )
      if (!nutrientResponse.success) {
        throw new Error(nutrientResponse.error || 'Nutrient fetch failed')
      }
      const nutrientData = nutrientResponse.data
      const food = Array.isArray(nutrientData?.foods) ? nutrientData.foods[0] : null
      if (!food) {
        throw new Error('No nutrient data')
      }

      const foodItem = {
        item_name: food.item_name || food.food_name || item.item_name || item.food_name || '',
        brand_name: food.brand_name ?? item.brand_name ?? null,
        thumbnail: food.photo?.thumb || item.thumbnail || item.photo?.thumb || item.photo || null,
        nutrient_name: 'Calories',
        calories: food.calories ?? food.nf_calories ?? 0,
        nutrient_value: '',
        nutrient_uom: 'kcal',
        serving_qty: food.serving_qty ?? food.qty ?? item.serving_qty ?? item.qty ?? 1,
        serving_uom: food.serving_unit || food.serving_uom || item.serving_uom || item.serving_unit || '',
        resource_id: item.resource_id || item.nix_item_id || item.tag_id || food.tag_id || '',
        nutrients: food.nutrients ?? null,
        brand_type: item.brand_type ?? food.brand_type ?? null,
        nix_item_id: food.nix_item_id ?? item.nix_item_id ?? null,
        food_name: food.food_name || item.food_name || item.item_name || '',
        serving_unit: food.serving_unit || food.serving_uom || item.serving_unit || item.serving_uom || '',
        serving_weight_grams: food.serving_weight_grams ?? food.serving_weight ?? null,
        nf_metric_qty: food.nf_metric_qty ?? null,
        nf_metric_uom: food.nf_metric_uom ?? null,
        nf_calories: food.nf_calories ?? food.calories ?? 0,
        nf_total_fat: food.nf_total_fat ?? food.fat ?? 0,
        nf_saturated_fat: food.nf_saturated_fat ?? food.saturated_fat ?? 0,
        nf_cholesterol: food.nf_cholesterol ?? food.cholesterol ?? 0,
        nf_sodium: food.nf_sodium ?? food.sodium ?? 0,
        nf_total_carbohydrate: food.nf_total_carbohydrate ?? food.carbs ?? 0,
        nf_dietary_fiber: food.nf_dietary_fiber ?? food.fiber ?? 0,
        nf_sugars: food.nf_sugars ?? food.sugars ?? 0,
        nf_protein: food.nf_protein ?? food.protein ?? 0,
        nf_potassium: food.nf_potassium ?? food.potassium ?? 0,
        nf_p: food.nf_p ?? food.phosphorus ?? null,
        nix_brand_name: food.nix_brand_name ?? item.brand_name ?? null,
        nix_brand_id: food.nix_brand_id ?? item.nix_brand_id ?? null,
        nix_item_name: food.nix_item_name ?? item.nix_item_name ?? food.food_name ?? null,
        metadata: food.metadata ?? {},
        source: food.source ?? item.source ?? null,
        ndb_no: food.ndb_no ?? null,
        alt_measures: food.alt_measures ?? [],
        lat: food.lat ?? null,
        lng: food.lng ?? null,
        photo: food.photo ?? item.photo ?? null,
        note: food.note ?? null,
        class_code: food.class_code ?? null,
        brick_code: food.brick_code ?? null,
        tag_id: food.tag_id ?? item.tag_id ?? null,
        updated_at: food.updated_at ?? null,
        unit: food.unit || food.serving_unit || item.unit || item.serving_unit || '',
        qty: food.qty ?? item.qty ?? 1,
        name: food.name || food.food_name || item.item_name || '',
        fat: food.fat ?? food.nf_total_fat ?? 0,
        carbs: food.carbs ?? food.nf_total_carbohydrate ?? 0,
        protein: food.protein ?? food.nf_protein ?? 0,
        sugars: food.sugars ?? food.nf_sugars ?? 0,
        fiber: food.fiber ?? food.nf_dietary_fiber ?? 0,
        calcium: food.calcium ?? null,
        iron: food.iron ?? null,
        potassium: food.potassium ?? null,
        sodium: food.sodium ?? null,
        vitamin_d: food.vitamin_d ?? null,
        cholesterol: food.cholesterol ?? food.nf_cholesterol ?? 0,
        fatty_acids: food.fatty_acids ?? null,
        is_added_manual: 1,
        toggle: 1
      }

      const logDate = selectedDate
        ? (() => {
            const d = new Date(selectedDate)
            const y = d.getFullYear()
            const m = `${d.getMonth() + 1}`.padStart(2, '0')
            const day = `${d.getDate()}`.padStart(2, '0')
            return `${y}-${m}-${day}`
          })()
        : undefined

      const createResponse = await apiService.createLogNutritionFood(companyUrl, {
        meal_id: meal.meal_id,
        mealplan_id: mealplan.id,
        log_qty: 1,
        isnutritionfood: true,
        program_id: selectedProgram?.id,
        foodItems: [foodItem],
        ...(logDate ? { logdate: logDate } : {}),
        mealfoods: [{ log_qty: 1 }]
      })
      if (!createResponse.success) {
        throw new Error(createResponse.error || 'Create log failed')
      }

      setSearchResults(prev => {
        const { [meal.meal_id]: _omit, ...rest } = prev
        return rest
      })
      setSearchCategories(prev => {
        const { [meal.meal_id]: _omit, ...rest } = prev
        return rest
      })
      setSearchQueries(prev => {
        const { [meal.meal_id]: _omit, ...rest } = prev
        return rest
      })

      await fetchNutritionData(companyUrl, selectedDate, selectedProgram?.id?.toString())
      toast({
        title: 'Food added',
        description: `${foodItem.item_name} added to ${meal.name}`,
        variant: 'success'
      })
    } catch (error) {
      toast({
        title: 'Add failed',
        description: error.message || 'Could not add food',
        variant: 'destructive'
      })
      console.error('Add food from search failed', error)
    } finally {
      setSearchSelectLoading(prev => {
        const { [meal.meal_id]: _omit, ...rest } = prev
        return rest
      })
    }
  }

  const getSearchListForMeal = (mealId) => {
    const category = searchCategories[mealId] || 'common'
    const results = searchResults[mealId]?.[category] || []
    return { results, category }
  }

  const getMealCalories = (meal, logged = false) => {
    return meal.foods.reduce((total, food) => {
      const id = food.mealfood_id || food.lognutritionsinglefoods_id || food.id
      if (logged && !loggedFoods[id]) return total
      const planQty = food.qty || 1
      const quantity = foodQuantities[id] ?? food.log_qty ?? food.qty ?? 1
      return total + ((food.calories / planQty) * quantity)
    }, 0)
  }

  // Chart data preparation
  const nutritionChartData = [
    {
      name: 'Calories',
      scheduled: Math.round(scheduledTotals.calories),
      logged: Math.round(loggedTotals.calories),
      unit: 'kcal'
    },
    {
      name: 'Protein',
      scheduled: Math.round(scheduledTotals.protein),
      logged: Math.round(loggedTotals.protein),
      unit: 'g'
    },
    {
      name: 'Carbs',
      scheduled: Math.round(scheduledTotals.carbs),
      logged: Math.round(loggedTotals.carbs),
      unit: 'g'
    },
    {
      name: 'Fat',
      scheduled: Math.round(scheduledTotals.fat),
      logged: Math.round(loggedTotals.fat),
      unit: 'g'
    }
  ]

  const pieChartData = [
    { name: 'Protein', value: loggedTotals.protein * 4, color: '#1E40AF' },
    { name: 'Carbs', value: loggedTotals.carbs * 4, color: '#2563EB' },
    { name: 'Fat', value: loggedTotals.fat * 9, color: '#93C5FD' }
  ]

  const mealplans = nutritionData?.mealplans || []
  const totalTiles = [
    { label: 'Calories', logged: loggedTotals.calories, scheduled: scheduledTotals.calories, unit: 'kcal' },
    { label: 'Protein', logged: loggedTotals.protein, scheduled: scheduledTotals.protein, unit: 'g' },
    { label: 'Carbs', logged: loggedTotals.carbs, scheduled: scheduledTotals.carbs, unit: 'g' },
    { label: 'Fat', logged: loggedTotals.fat, scheduled: scheduledTotals.fat, unit: 'g' },
  ]
  const loggedCountOf = (meal) => meal.foods.filter((food) => resolveFoodState(food).resolvedLogged).length
  const macrosOf = (food, quantity) => {
    const scale = (value) => Math.round(((value || 0) / (food.qty || 1)) * quantity)
    return { calories: scale(food.calories), protein: scale(food.protein), carbs: scale(food.carbs), fat: scale(food.fat) }
  }
  const quantityButtonClass = 'flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50'

  return (
    <>
      <div className="app-page">
        <PageHeader title="Nutrition" subtitle={mealplans[0]?.name || undefined}>
          {programs.length > 0 && (
            <Select
              value={selectedProgram?.id?.toString() || ''}
              onValueChange={(value) => {
                const program = programs.find(p => p.id === parseInt(value))
                setSelectedProgram(program)
              }}
            >
              <SelectTrigger className="h-9 w-full text-sm sm:w-[270px]">
                <SelectValue placeholder="Select a program" />
              </SelectTrigger>
              <SelectContent>
                {programs.map(program => (
                  <SelectItem key={program.id} value={program.id.toString()}>
                    {program.goal.name} - {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            placeholder="Select date"
            className="h-9 w-[168px] justify-center"
          />
        </PageHeader>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => handleStaticLink('substitutionguide_url')} disabled={!staticLinks?.substitutionguide_url}>
            <Shuffle className="mr-2 h-4 w-4" />
            Substitution Guide
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleStaticLink('diningout_url')} disabled={!staticLinks?.diningout_url}>
            <UtensilsCrossed className="mr-2 h-4 w-4" />
            Dining Out Guide
          </Button>
          <Button variant="outline" size="sm" onClick={handleShoppingListPdf} disabled={shoppingListLoading}>
            {shoppingListLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShoppingBag className="mr-2 h-4 w-4" />}
            {shoppingListLoading ? 'Loading…' : 'Shopping List'}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrintPdf} disabled={printLoading}>
            {printLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
            {printLoading ? 'Loading…' : 'Print'}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading your nutrition data…</span>
          </div>
        ) : error ? (
          <div className="mx-auto max-w-sm py-12 text-center">
            <AlertCircle className="mx-auto mb-4 h-8 w-8 text-slate-500" />
            <h3 className="mb-1 text-base font-semibold text-slate-900">{error}</h3>
            <p className="mb-4 text-sm text-slate-500">Please pick a different program or date above, or try again.</p>
            <Button onClick={() => fetchClientPrograms(companyUrl)}>Try again</Button>
          </div>
        ) : (
          <>
            {/* Day totals — one bar each, one colour */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {totalTiles.map(({ label, logged, scheduled, unit }) => (
                <Card key={label} className="p-4">
                  <p className="text-xs font-semibold text-slate-500">{label}</p>
                  <p className="mt-1 whitespace-nowrap text-slate-900">
                    <span className="text-xl font-semibold tracking-[-0.01em]">{Math.round(logged).toLocaleString()}{unit === 'g' ? 'g' : ''}</span>
                    <span className="ml-1.5 text-sm text-slate-500">/ {Math.round(scheduled).toLocaleString()}{unit === 'g' ? 'g' : ` ${unit}`}</span>
                  </p>
                  <Progress value={scheduled > 0 ? (logged / scheduled) * 100 : 0} className="mt-3" aria-label={`${label} logged`} />
                </Card>
              ))}
            </div>

            {/* Meal plans */}
            {mealplans.map((mealplan) => (
              <div key={mealplan.id} className="space-y-3">
                {mealplans.length > 1 && (
                  <div className="flex items-baseline justify-between pt-1">
                    <h2 className="app-kicker">{mealplan.name}</h2>
                    <span className="text-xs text-slate-500">
                      {mealplan.meals.length} meals · {Math.round(mealplan.meals.reduce((total, meal) => total + getMealCalories(meal, true), 0))} kcal logged
                    </span>
                  </div>
                )}

                {mealplan.meals.map((meal) => {
                  const isExpanded = expandedMeals[meal.meal_id]
                  const searchState = getSearchListForMeal(meal.meal_id)

                  return (
                    <Card key={meal.meal_id} className="overflow-hidden">
                      <button
                        type="button"
                        aria-expanded={Boolean(isExpanded)}
                        onClick={() => toggleMealExpansion(meal.meal_id)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-background"
                      >
                        <div className="min-w-0">
                          <h3 className="truncate text-[15px] font-semibold text-slate-900">
                            {meal.name}
                            <span className="ml-2 text-sm font-normal text-slate-500">
                              {Math.round(getMealCalories(meal, true))} / {Math.round(getMealCalories(meal))} kcal
                            </span>
                          </h3>
                          {meal.time && <p className="mt-0.5 text-xs text-slate-500">{meal.time}</p>}
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-3">
                          <span className="text-xs text-slate-500">{loggedCountOf(meal)} of {meal.foods.length} logged</span>
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="border-t border-slate-200">
                          {meal.foods.map((food) => {
                            const { foodId, resolvedLogged, resolvedQuantity, noteValue: currentNote } = resolveFoodState(food)
                            const isEditingQty = Object.prototype.hasOwnProperty.call(editingQuantity, foodId)
                            const originalQuantity = editingQuantity[foodId]
                            const isCustom = food.is_added_manual === 1 || food.is_added_manual === '1'
                            const macros = macrosOf(food, resolvedQuantity)

                            return (
                              <div
                                key={foodId}
                                className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-100 px-4 py-2.5 hover:bg-background"
                              >
                                <button
                                  type="button"
                                  role="checkbox"
                                  aria-checked={resolvedLogged}
                                  aria-label={`${resolvedLogged ? 'Unlog' : 'Log'} ${food.name}`}
                                  onClick={() => handleFoodToggle(food, meal, mealplan)}
                                  className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[5px] border transition-colors ${
                                    resolvedLogged ? 'border-primary bg-primary text-white' : 'border-slate-300 bg-white hover:border-slate-400'
                                  }`}
                                >
                                  {resolvedLogged && <Check className="h-3 w-3" strokeWidth={3} />}
                                </button>

                                <div className="flex min-w-0 flex-1 basis-40 flex-wrap items-center gap-x-2">
                                  <span className="truncate text-sm text-slate-900">{food.name}</span>
                                  {food.cat_name && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{food.cat_name}</span>}
                                  {isCustom && <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-blue-800">Custom</span>}
                                </div>

                                {/* Quantity */}
                                {isEditingQty ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="number"
                                      step="0.1"
                                      min="0"
                                      value={resolvedQuantity}
                                      onChange={(e) => updateFoodQuantity(foodId, parseFloat(e.target.value) || 0)}
                                      className="h-8 w-20"
                                      aria-label={`Quantity of ${food.name}`}
                                      autoFocus
                                    />
                                    <button
                                      type="button"
                                      title="Save quantity"
                                      className={quantityButtonClass}
                                      onClick={async () => {
                                        await handleQuantityChange(food, meal, mealplan, foodId, resolvedQuantity, resolvedLogged, currentNote)
                                      }}
                                    >
                                      <Save className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      title="Cancel"
                                      className={quantityButtonClass}
                                      onClick={() => {
                                        const fallbackQuantity = Number(food.log_qty ?? food.qty ?? 0) || 0
                                        const quantityToRestore = originalQuantity !== undefined ? originalQuantity : fallbackQuantity
                                        updateFoodQuantity(foodId, quantityToRestore)
                                        setEditingQuantity(prev => {
                                          const { [foodId]: _omit, ...rest } = prev
                                          return rest
                                        })
                                      }}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      title="Decrease quantity"
                                      className={quantityButtonClass}
                                      onClick={async () => {
                                        const updatedQuantity = Math.max(0, resolvedQuantity - 0.5)
                                        await handleQuantityChange(food, meal, mealplan, foodId, updatedQuantity, resolvedLogged, currentNote)
                                      }}
                                    >
                                      <Minus className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      title="Edit quantity"
                                      className="min-w-[4.5rem] rounded-md px-1.5 py-1 text-center text-sm text-slate-700 transition-colors hover:bg-slate-100 hover:text-primary"
                                      onClick={() => setEditingQuantity(prev => ({ ...prev, [foodId]: resolvedQuantity }))}
                                    >
                                      {resolvedQuantity} {food.unit}
                                    </button>
                                    <button
                                      type="button"
                                      title="Increase quantity"
                                      className={quantityButtonClass}
                                      onClick={async () => {
                                        const updatedQuantity = resolvedQuantity + 0.5
                                        await handleQuantityChange(food, meal, mealplan, foodId, updatedQuantity, resolvedLogged, currentNote)
                                      }}
                                    >
                                      <Plus className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                )}

                                <div className="flex items-baseline gap-3 whitespace-nowrap text-sm">
                                  <span className="w-[4.5rem] text-right text-slate-900">{macros.calories} kcal</span>
                                  <span className="w-9 text-right text-slate-500">{macros.protein}P</span>
                                  <span className="w-9 text-right text-slate-500">{macros.carbs}C</span>
                                  <span className="w-9 text-right text-slate-500">{macros.fat}F</span>
                                </div>

                                <div className="flex w-[3.75rem] items-center justify-end">
                                  <button
                                    type="button"
                                    title="Note"
                                    aria-label={`Note for ${food.name}`}
                                    onClick={() => openNoteModal(food, meal, mealplan)}
                                    className={`rounded-lg p-1.5 transition-colors hover:bg-slate-100 ${currentNote ? 'text-primary' : 'text-slate-500 hover:text-slate-700'}`}
                                  >
                                    <StickyNote className="h-4 w-4" />
                                  </button>
                                  {isCustom && (
                                    <button
                                      type="button"
                                      title="Delete food"
                                      aria-label={`Delete ${food.name}`}
                                      onClick={() => handleDeleteCustomFood(food, mealplan)}
                                      disabled={deleteLoading[foodId]}
                                      className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-red-600 disabled:opacity-50"
                                    >
                                      {deleteLoading[foodId] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                    </button>
                                  )}
                                </div>
                              </div>
                            )
                          })}

                          {/* Search for additional foods */}
                          <div className="space-y-3 p-4">
                            <div className="relative">
                              <Input
                                value={searchQueries[meal.meal_id] || ''}
                                onChange={(e) => handleSearchQueryChange(meal.meal_id, e.target.value)}
                                placeholder="Search for additional foods"
                                aria-label={`Search for additional foods for ${meal.name}`}
                                className="h-10"
                              />
                              {searchLoading[meal.meal_id] && (
                                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-500" />
                              )}
                            </div>

                            {searchResults[meal.meal_id] && (
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant={(searchCategories[meal.meal_id] || 'common') === 'common' ? 'default' : 'outline'}
                                    onClick={() => setSearchCategories(prev => ({ ...prev, [meal.meal_id]: 'common' }))}
                                  >
                                    Common ({searchResults[meal.meal_id].common.length})
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={(searchCategories[meal.meal_id] || 'common') === 'branded' ? 'default' : 'outline'}
                                    onClick={() => setSearchCategories(prev => ({ ...prev, [meal.meal_id]: 'branded' }))}
                                  >
                                    Branded ({searchResults[meal.meal_id].branded.length})
                                  </Button>
                                </div>
                                <div className="grid gap-2 md:grid-cols-2">
                                  {searchState.results.length ? (
                                    searchState.results.map((item, idx) => (
                                      <button
                                        key={`${item.resource_id || item.nix_item_id || item.item_name}-${idx}`}
                                        type="button"
                                        onClick={() => handleSearchResultSelect(meal, mealplan, item)}
                                        disabled={searchSelectLoading[meal.meal_id]}
                                        className={`flex items-center gap-3 rounded-[10px] border border-slate-200 bg-white p-3 text-left transition-colors hover:bg-background ${searchSelectLoading[meal.meal_id] ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                      >
                                        {item.thumbnail && (
                                          <img src={item.thumbnail} alt={item.item_name} className="h-10 w-10 rounded-md object-cover" />
                                        )}
                                        <div className="min-w-0 flex-1">
                                          <div className="truncate text-sm font-medium text-slate-900">{item.item_name}</div>
                                          <div className="text-xs text-slate-500">
                                            {(item.brand_name || 'Common') + ' • ' + (item.serving_qty || '') + ' ' + (item.serving_uom || '')}
                                          </div>
                                        </div>
                                        {searchSelectLoading[meal.meal_id] && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="text-sm text-slate-500">No items found</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Card>
                  )
                })}
              </div>
            ))}

            {/* Calorie composition */}
            {mealplans.length > 0 && (
              <Card className="overflow-hidden">
                <div className="border-b border-slate-200 px-4 py-3">
                  <h2 className="text-[15px] font-semibold text-slate-900">Calorie composition</h2>
                </div>
                {pieChartData.every(d => d.value === 0) ? (
                  <p className="px-4 py-8 text-center text-sm text-slate-500">No foods logged yet.</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-6 p-4">
                    <div className="h-[170px] w-[170px] flex-none">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={48} outerRadius={78} paddingAngle={2} dataKey="value" stroke="none">
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [`${Math.round(value)} kcal`, 'From macros']} />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="min-w-[200px] flex-1 space-y-2.5">
                      {pieChartData.map((entry) => {
                        const total = pieChartData.reduce((sum, d) => sum + d.value, 0)
                        const share = total > 0 ? Math.round((entry.value / total) * 100) : 0
                        return (
                          <div key={entry.name} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2 text-slate-700">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                              {entry.name}
                            </span>
                            <span>
                              <span className="font-semibold text-slate-900">{share}%</span>
                              <span className="ml-1.5 text-xs text-slate-500">{Math.round(entry.value)} kcal</span>
                            </span>
                          </div>
                        )
                      })}
                      <div className="flex justify-between border-t border-slate-100 pt-2 text-sm">
                        <span className="text-slate-500">Total</span>
                        <span className="font-semibold text-slate-900">{Math.round(loggedTotals.calories)} kcal</span>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Empty state */}
            {!mealplans.length && !isLoading && (
              <Card>
                <CardContent className="py-12 text-center">
                  <h3 className="mb-1 text-base font-semibold text-slate-900">No meal plans found</h3>
                  <p className="text-sm text-slate-500">No meal plans are available for the selected program and date.</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

    <Dialog open={noteModal.open} onOpenChange={(open) => { if (!open) closeNoteModal() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Note</DialogTitle>
        </DialogHeader>
        <Textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Add a note"
          className="min-h-[200px]"
        />
        <DialogFooter className="flex flex-row justify-end space-x-2">
          <Button variant="outline" onClick={closeNoteModal} disabled={noteSaving}>
            Cancel
          </Button>
          <Button onClick={submitNoteModal} disabled={noteSaving}>
            {noteSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}

export default ClientNutrition
